import { useEffect, useState } from 'react';
import { LoanApi } from '@apply/api';
import { LoanStatusType } from '../../../common/LoanConstants';
import { applicationInsights } from '../../../../ApplicationInsights';
import { ILoanSummaryDashboardData, ILoanSummaryDashboardRequest } from '@apply/api/src/api/loanApi';
import { DashboardClosedByMonth } from './outstanding/components/DashboardClosedByMonth/DashboardClosedByMonth';
import { DashboardClosedByPersonnel } from './outstanding/components/DashboardClosedByPersonnel/DashboardClosedByPersonnel';
import { DashboardClosedByMonthPivotMode } from './constants/DashboardClosedByMonthPivotMode';
import { DashboardClosedByPOCPivotMode } from './constants/DashboardClosedByPOCPivotMode';
import { closedLoanTypeByMonthTotals } from './constants/closedLoanTypeByMonthTotals';
import { DashboardOutstandingByMonth } from './outstanding/components/DashboardOutstandingByMonth/DashboardOutstandingByMonth';
import { DashboardOutstandingByMonthPivotMode } from './constants/DashboardOutstandingByMonthPivotMode';
import { DashboardOutstandingByPersonnel } from './outstanding/components/DashboardOutstandingByPersonnel/DashboardOutstandingByPersonnel';
import { DashboardOutstandingByPOCPivotMode } from './constants/DashboardOutstandingByPOCPivotMode';
import { outstandingLoanTypeTotals } from './constants/outstandingLoanTypeTotals';
import { ExportDashboardButton } from './outstanding/components/ExportDashboardButton';
import { ScopedWaitOverlay } from '@apply/components';

export interface LoansSummaryDashboardProps {
    status: LoanStatusType;
    set_headerActions: (actions: React.ReactNode) => void;
    isActive: boolean;
}

export const LoansSummaryDashboard = ({ status, set_headerActions, isActive }: LoansSummaryDashboardProps) => {
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const [isLoading, set_isLoading] = useState(true);

    const [dashboardData, set_dashboardData] = useState<ILoanSummaryDashboardData>();

    const [closedState, setClosedState] = useState({
        byMonthPivotMode: DashboardClosedByMonthPivotMode.Committed,
        byPocPivotMode: DashboardClosedByPOCPivotMode.Committed,
        pocByMonthClosedMonth: 'MonthToDate' as string | null,
        availableClosedMonths: [] as { key: string; display: string }[],
    });

    const [outstandingState, setOutstandingState] = useState({
        byMonthPivotMode: DashboardOutstandingByMonthPivotMode.Committed,
        byPocPivotMode: DashboardOutstandingByPOCPivotMode.Committed,
    });

    useEffect(() => {
        const loadData = async () => {
            const criteria: ILoanSummaryDashboardRequest = {
                statusTypeId: status,
            };

            const response = await loanApi.getLoanSummaryDashboardData(criteria);
            set_dashboardData(response.result);
        };

        loadData();
    }, [status]);

    useEffect(() => {
        const summary = dashboardData?.closedData.loanTypeSummary;
        if (!summary?.length) {
            setClosedState((prev) => ({ ...prev, availableClosedMonths: [] }));
            return;
        }

        const first = summary[0];

        const months = [
            { key: 'MonthToDate', display: 'Month To Date' },
            { key: 'T-12', display: 'T-12' },
            ...first.monthlyDetails.map((m) => ({
                key: m.month,
                display: m.monthFullName,
            })),
        ];

        setClosedState((prev) => ({ ...prev, availableClosedMonths: months }));
    }, [dashboardData]);

    useEffect(() => {
        if (isActive) {
            set_headerActions(
                <ExportDashboardButton
                    closed={closedState}
                    outstanding={outstandingState}
                />
            );
        }
    }, [closedState, outstandingState, set_headerActions, isActive]);

    useEffect(() => {
        if (!dashboardData) return;
        // Page has data, grids are rendering underneath.
        // Keep mask for a brief moment to avoid flicker.
        requestAnimationFrame(() => {
            setTimeout(() => {
                set_isLoading(false);
            }, 250);
        });
    }, [dashboardData]);

    return (
        <div
            className='main-content'
            style={{ padding: 0, backgroundColor: 'white' }}
        >
            <div
                className='scrollable-content'
                style={{ padding: '1rem' }}
            >
                <DashboardClosedByMonth
                    pivotMode={closedState.byMonthPivotMode}
                    set_pivotMode={(m) => setClosedState((prev) => ({ ...prev, byMonthPivotMode: m }))}
                    dataSource={dashboardData?.closedData.loanTypeSummary}
                />

                <DashboardClosedByPersonnel
                    pivotMode={closedState.byPocPivotMode}
                    set_pivotMode={(m) => setClosedState((prev) => ({ ...prev, byPocPivotMode: m }))}
                    dataSource={dashboardData?.closedData.pocPivotSummary}
                    loanTypeClosedMonthlyTotals={closedLoanTypeByMonthTotals(dashboardData?.closedData.loanTypeSummary)}
                    closedMonth={closedState.pocByMonthClosedMonth}
                    set_closedMonth={(m) => setClosedState((prev) => ({ ...prev, pocByMonthClosedMonth: m }))}
                    dashboardClosedAvailableClosedMonths={closedState.availableClosedMonths}
                />

                <DashboardOutstandingByMonth
                    pivotMode={outstandingState.byMonthPivotMode}
                    set_pivotMode={(m) => setOutstandingState((prev) => ({ ...prev, byMonthPivotMode: m }))}
                    dataSource={dashboardData?.outstandingData.loanTypeSummary}
                />

                <DashboardOutstandingByPersonnel
                    pivotMode={outstandingState.byPocPivotMode}
                    set_pivotMode={(m) => setOutstandingState((prev) => ({ ...prev, byPocPivotMode: m }))}
                    dataSource={dashboardData?.outstandingData.pocPivotSummary}
                    loanTypeOustandingTotals={outstandingLoanTypeTotals(dashboardData?.outstandingData.loanTypeSummary)}
                />
            </div>
            <ScopedWaitOverlay
                visible={isLoading}
                label='Retrieving Loans and Building Your Dashboard...'
                mode='Admin'
            />
        </div>
    );
};
